// client/src/components/DeepCodeAnalyzer.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  LinearProgress,
  Chip,
  Alert,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  CircularProgress,
  Snackbar
} from '@mui/material';
import {
  PlayArrow,
  Pause,
  Refresh,
  Code,
  BugReport,
  TrendingUp,
  Security,
  Storage,
  Download,
  ExpandMore,
  ExpandLess,
  Visibility,
  Assessment,
  Close
} from '@mui/icons-material';
import apiTester from '../api/apiTester'; // Используем существующий apiTester

const DeepCodeAnalyzer = () => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoAnalyze, setAutoAnalyze] = useState(false);
  const [selectedModule, setSelectedModule] = useState('');
  const [logLines, setLogLines] = useState(100);
  const [logSource, setLogSource] = useState('imcon');
  const [detailedView, setDetailedView] = useState({});
  const [reportDialog, setReportDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  
  // Список модулей для анализа
  const modules = [
    'ImCon.EventSystem',
    'ImCon.RuleEngine',
    'ImCon.Storage.MongoDB',
    'CharacterDesigner.Trainer',
    'SimCharacter.Manager',
    'WorldManager.World',
    'UniverseGateway.ShipManager',
    'ImageLang.Encoder'
  ];

  const logSources = [
    { value: 'imcon', label: 'ImCon' },
    { value: 'universe_gateway', label: 'Universe Gateway' },
    { value: 'world_manager', label: 'World Manager' },
    { value: 'sim_character', label: 'Sim Character' },
    { value: 'character_designer', label: 'Character Designer' }
  ];

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const startDeepAnalysis = async () => {
    setLoading(true);
    try {
      // Используем apiTester для отправки запроса
      const result = await apiTester.sendRestRequest(
        'imcon',
        '/api/health/deep-code-analysis',
        'POST'
      );
      
      setAnalysis(result.data);
      showSnackbar('Глубокий анализ запущен успешно', 'success');
    } catch (error) {
      console.error('Analysis failed:', error);
      showSnackbar(`Ошибка анализа: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const analyzeModule = async (moduleName) => {
    if (!moduleName) {
      showSnackbar('Выберите модуль для анализа', 'warning');
      return;
    }

    setLoading(true);
    try {
      const result = await apiTester.sendRestRequest(
        'imcon',
        '/api/health/auto-test-module',
        'POST',
        { module_name: moduleName }
      );
      
      // Обновляем или добавляем результат
      setAnalysis(prev => ({
        ...prev,
        components: {
          ...prev?.components,
          module_tests: {
            ...prev?.components?.module_tests,
            [moduleName]: result.data
          }
        }
      }));
      
      showSnackbar(`Анализ модуля ${moduleName} завершен`, 'success');
    } catch (error) {
      console.error('Module analysis failed:', error);
      showSnackbar(`Ошибка анализа модуля: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const analyzeLogs = async () => {
    if (!logSource) {
      showSnackbar('Выберите источник логов', 'warning');
      return;
    }

    setLoading(true);
    try {
      const result = await apiTester.sendRestRequest(
        'imcon',
        '/api/health/analyze-logs',
        'POST',
        { 
          source: logSource,
          lines: logLines
        }
      );
      
      setAnalysis(prev => ({
        ...prev,
        components: {
          ...prev?.components,
          log_analysis: {
            ...prev?.components?.log_analysis,
            [logSource]: result.data
          }
        }
      }));
      
      showSnackbar(`Анализ логов ${logSource} завершен`, 'success');
    } catch (error) {
      console.error('Log analysis failed:', error);
      showSnackbar(`Ошибка анализа логов: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const exportAnalysisReport = () => {
    if (!analysis) {
      showSnackbar('Нет данных для экспорта', 'warning');
      return;
    }
    
    try {
      const dataStr = JSON.stringify(analysis, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `deep-analysis-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      showSnackbar('Отчет успешно экспортирован', 'success');
    } catch (error) {
      console.error('Export failed:', error);
      showSnackbar(`Ошибка экспорта: ${error.message}`, 'error');
    }
  };

  // Автоматический анализ
  useEffect(() => {
    if (!autoAnalyze) return;
    
    const interval = setInterval(() => {
      startDeepAnalysis();
    }, 300000); // Каждые 5 минут
    
    return () => clearInterval(interval);
  }, [autoAnalyze]);

  const renderHealthScore = (score) => {
    let color = 'error';
    if (score >= 70) color = 'success';
    else if (score >= 40) color = 'warning';
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LinearProgress 
          variant="determinate" 
          value={score} 
          sx={{ width: 100, height: 10, borderRadius: 5 }}
          color={color}
        />
        <Typography variant="body2">{score}/100</Typography>
      </Box>
    );
  };

  const renderIssueChips = (issues) => {
    if (!issues || issues.length === 0) {
      return <Chip label="Нет проблем" color="success" size="small" />;
    }
    
    return (
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {issues.map((issue, idx) => (
          <Chip 
            key={idx}
            label={issue.length > 30 ? `${issue.substring(0, 30)}...` : issue}
            color="error"
            size="small"
            title={issue}
          />
        ))}
      </Box>
    );
  };

  const renderRecommendations = (recommendations) => {
    if (!recommendations || recommendations.length === 0) {
      return <Typography variant="body2" color="textSecondary">Нет рекомендаций</Typography>;
    }
    
    return (
      <Box sx={{ mt: 1 }}>
        {recommendations.map((rec, idx) => (
          <Alert 
            key={idx}
            severity="info"
            sx={{ mb: 1 }}
          >
            {rec}
          </Alert>
        ))}
      </Box>
    );
  };

  const renderModuleAnalysis = (moduleName, data) => {
    const isExpanded = detailedView[moduleName];
    
    return (
      <Card key={moduleName} sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              <Code sx={{ verticalAlign: 'middle', mr: 1 }} />
              {moduleName}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {data.analysis?.quality_score && renderHealthScore(data.analysis.quality_score)}
              <IconButton onClick={() => setDetailedView(prev => ({ ...prev, [moduleName]: !isExpanded }))}>
                {isExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
          </Box>
          
          <Collapse in={isExpanded}>
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Тестовые сценарии: {data.test_scenarios_count || 0}
                  </Typography>
                </Grid>
                
                {data.analysis && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2">Оценка качества:</Typography>
                      {data.analysis.quality_score && renderHealthScore(data.analysis.quality_score)}
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Typography variant="subtitle2">Рекомендации:</Typography>
                      {renderRecommendations(data.analysis.recommendations)}
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Typography variant="subtitle2">Потрачено токенов: {data.tokens_used || 0}</Typography>
                    </Grid>
                  </>
                )}
                
                {data.error && (
                  <Grid item xs={12}>
                    <Alert severity="error">{data.error}</Alert>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Collapse>
        </CardContent>
      </Card>
    );
  };

  const renderLogAnalysis = (source, data) => {
    const isExpanded = detailedView[`log_${source}`];
    
    return (
      <Card key={source} sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              <Storage sx={{ verticalAlign: 'middle', mr: 1 }} />
              Логи: {logSources.find(s => s.value === source)?.label || source}
            </Typography>
            <IconButton onClick={() => setDetailedView(prev => ({ ...prev, [`log_${source}`]: !isExpanded }))}>
              {isExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
          
          <Typography variant="body2" color="textSecondary">
            Проанализировано строк: {data.log_lines || 0}
          </Typography>
          
          <Collapse in={isExpanded}>
            <Box sx={{ mt: 2 }}>
              {data.analysis && (
                <>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2">Критические ошибки:</Typography>
                      {renderIssueChips(data.analysis.critical_errors)}
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Typography variant="subtitle2">Предупреждения:</Typography>
                      {renderIssueChips(data.analysis.warnings)}
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Typography variant="subtitle2">Проблемы производительности:</Typography>
                      {renderIssueChips(data.analysis.performance_issues)}
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Typography variant="subtitle2">Рекомендации:</Typography>
                      {renderRecommendations(data.analysis.recommendations)}
                    </Grid>
                  </Grid>
                </>
              )}
            </Box>
          </Collapse>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          <Assessment sx={{ verticalAlign: 'middle', mr: 2 }} />
          Глубокий анализ кода с AI
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            onClick={startDeepAnalysis}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <PlayArrow />}
          >
            {loading ? 'Анализ...' : 'Запустить анализ'}
          </Button>
          
          <Button
            variant={autoAnalyze ? "contained" : "outlined"}
            color={autoAnalyze ? "warning" : "primary"}
            onClick={() => setAutoAnalyze(!autoAnalyze)}
            startIcon={autoAnalyze ? <Pause /> : <PlayArrow />}
          >
            {autoAnalyze ? 'Остановить автоанализ' : 'Автоанализ'}
          </Button>
          
          <Button
            variant="outlined"
            onClick={exportAnalysisReport}
            disabled={!analysis}
            startIcon={<Download />}
          >
            Экспорт отчета
          </Button>
          
          <Button
            variant="outlined"
            onClick={() => setReportDialog(true)}
            disabled={!analysis}
            startIcon={<Visibility />}
          >
            Подробный отчет
          </Button>
        </Box>
      </Box>
      
      {/* Панель управления */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Модуль для анализа</InputLabel>
                <Select
                  value={selectedModule}
                  onChange={(e) => setSelectedModule(e.target.value)}
                  label="Модуль для анализа"
                >
                  <MenuItem value="">
                    <em>Выберите модуль</em>
                  </MenuItem>
                  {modules.map(module => (
                    <MenuItem key={module} value={module}>
                      {module}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Строк логов"
                value={logLines}
                onChange={(e) => setLogLines(parseInt(e.target.value) || 100)}
                InputProps={{ inputProps: { min: 10, max: 1000 } }}
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Источник логов</InputLabel>
                <Select
                  value={logSource}
                  onChange={(e) => setLogSource(e.target.value)}
                  label="Источник логов"
                >
                  {logSources.map(source => (
                    <MenuItem key={source.value} value={source.value}>
                      {source.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={2} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Button
                variant="contained"
                onClick={() => analyzeModule(selectedModule)}
                disabled={!selectedModule || loading}
                fullWidth
              >
                Анализ модуля
              </Button>
              
              <Button
                variant="outlined"
                onClick={analyzeLogs}
                disabled={loading}
                fullWidth
              >
                Анализ логов
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      
      {/* Основные метрики */}
      {analysis && analysis.summary && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Общий счет здоровья
                </Typography>
                <Typography variant="h3">
                  {analysis.health_score || 0}
                </Typography>
                {renderHealthScore(analysis.health_score || 0)}
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Проанализировано компонентов
                </Typography>
                <Typography variant="h3">
                  {analysis.summary.components_analyzed || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Протестировано модулей
                </Typography>
                <Typography variant="h3">
                  {analysis.summary.modules_tested || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Потрачено токенов
                </Typography>
                <Typography variant="h3">
                  {analysis.summary.total_tokens_used || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      
      {/* Результаты анализа */}
      {analysis && (
        <>
          {/* Анализ модулей */}
          {analysis.components?.module_tests && Object.keys(analysis.components.module_tests).length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <Code sx={{ mr: 1 }} />
                Анализ модулей
              </Typography>
              
              {Object.entries(analysis.components.module_tests).map(([module, data]) => 
                renderModuleAnalysis(module, data)
              )}
            </Box>
          )}
          
          {/* Анализ логов */}
          {analysis.components?.log_analysis && Object.keys(analysis.components.log_analysis).length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <Storage sx={{ mr: 1 }} />
                Анализ логов
              </Typography>
              
              {Object.entries(analysis.components.log_analysis).map(([source, data]) => 
                renderLogAnalysis(source, data)
              )}
            </Box>
          )}
          
          {/* Рекомендации */}
          {analysis.recommendations && analysis.recommendations.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Рекомендации по улучшению
                </Typography>
                {renderRecommendations(analysis.recommendations)}
              </CardContent>
            </Card>
          )}
          
          {/* Сообщение, если нет данных */}
          {(!analysis.components?.module_tests || Object.keys(analysis.components.module_tests).length === 0) &&
           (!analysis.components?.log_analysis || Object.keys(analysis.components.log_analysis).length === 0) && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="body1" align="center" color="textSecondary">
                  Нет данных анализа. Запустите анализ модуля или логов для получения результатов.
                </Typography>
              </CardContent>
            </Card>
          )}
        </>
      )}
      
      {/* Сообщение, если анализ не запускался */}
      {!analysis && !loading && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" align="center" gutterBottom>
              Запустите анализ для получения результатов
            </Typography>
            <Typography variant="body1" align="center" color="textSecondary">
              Используйте кнопку "Запустить анализ" или настройте параметры анализа модулей/логов
            </Typography>
          </CardContent>
        </Card>
      )}
      
      {/* Диалог подробного отчета */}
      <Dialog
        open={reportDialog}
        onClose={() => setReportDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Подробный отчет анализа</Typography>
            <IconButton onClick={() => setReportDialog(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {analysis && (
            <Box sx={{ mt: 2 }}>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Компонент</TableCell>
                      <TableCell>Статус</TableCell>
                      <TableCell>Оценка</TableCell>
                      <TableCell>Проблемы</TableCell>
                      <TableCell>Токены</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* DeepSeek API */}
                    <TableRow>
                      <TableCell>DeepSeek API</TableCell>
                      <TableCell>
                        <Chip 
                          label={analysis.components?.deepseek?.status || 'unknown'} 
                          color={analysis.components?.deepseek?.status === 'healthy' ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>
                        {analysis.components?.deepseek?.error || 'Нет'}
                      </TableCell>
                      <TableCell>-</TableCell>
                    </TableRow>
                    
                    {/* Module Tests */}
                    {analysis.components?.module_tests && Object.entries(analysis.components.module_tests).map(([module, data]) => (
                      <TableRow key={module}>
                        <TableCell>{module}</TableCell>
                        <TableCell>
                          <Chip 
                            label={data.status || 'unknown'} 
                            color={data.status === 'healthy' ? 'success' : data.status === 'warning' ? 'warning' : 'error'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {data.analysis?.quality_score ? `${data.analysis.quality_score}/100` : '-'}
                        </TableCell>
                        <TableCell>
                          {data.analysis?.issues?.length || data.error ? 'Да' : 'Нет'}
                        </TableCell>
                        <TableCell>{data.tokens_used || 0}</TableCell>
                      </TableRow>
                    ))}
                    
                    {/* Log Analysis */}
                    {analysis.components?.log_analysis && Object.entries(analysis.components.log_analysis).map(([source, data]) => (
                      <TableRow key={source}>
                        <TableCell>Логи: {logSources.find(s => s.value === source)?.label || source}</TableCell>
                        <TableCell>
                          <Chip 
                            label={data.status || 'unknown'} 
                            color={data.status === 'healthy' ? 'success' : data.status === 'warning' ? 'warning' : 'error'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {data.analysis?.health_score ? `${data.analysis.health_score}/100` : '-'}
                        </TableCell>
                        <TableCell>
                          {data.analysis?.critical_errors?.length || data.error ? 'Да' : 'Нет'}
                        </TableCell>
                        <TableCell>{data.tokens_used || 0}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportDialog(false)}>Закрыть</Button>
          <Button onClick={exportAnalysisReport} variant="contained">
            Экспорт отчета
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar для уведомлений */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        action={
          <IconButton
            size="small"
            color="inherit"
            onClick={() => setSnackbar({ ...snackbar, open: false })}
          >
            <Close fontSize="small" />
          </IconButton>
        }
      />
    </Box>
  );
};

export default DeepCodeAnalyzer;